<template>
  <PModal :title="title" :model-value="modelValue" @update:model-value="emit('update:modelValue', $event)">
    <template #activator="{}">
      <span class="hidden"></span>
    </template>

    <div v-if="!link" class="flex items-center gap-2">
      <span>Génération du lien de téléchargement en cours...</span>
      <PSpinner size="lg" enabled />
    </div>
    <div v-else-if="link && mailto">
      <a :href="`mailto:?body=${encodeURIComponent(link)}`">Envoyer par courrier</a>
    </div>
    <div v-else>
      <PLink :to="link" @click="emit('update:modelValue', false)">
        {{ downloadLabel }}
      </PLink>
    </div>
  </PModal>
</template>

<script setup lang="ts">
import { PModal, PSpinner, PLink } from "@pasta/box-v2";
import { computed, ref, watch } from "vue";
import { useLetterFileStore } from "../stores/letter-file.store";
import { useLetterFileFolderApi } from "../http/letter-file-folder.api";
import { useLetterFileDocumentApi } from "../http/letter-file-document.api";
import dayjs from "dayjs";
import { useSimpleFeatureStore } from "../stores/simple-features.store";
import { LinkPurpose } from "../dto/letter-file-document.dto";

interface Props {
  modelValue: boolean;
  id: number;
  type: "document" | "folder" | "letterFile";
  mailto?: boolean;
  disabled?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const title = computed(() => (link.value ? "Fichiers prêts" : "Veuillez patienter"));
const downloadLabel = computed(() => {
  switch (props.type) {
    case "document":
      return "Télécharger le document";
    case "folder":
      return "Télécharger les documents";
    default:
    case "letterFile":
      return "Télécharger les documents";
  }
});

const simpleFeatureStore = useSimpleFeatureStore();
const letterFileStore = useLetterFileStore();
const documentApi = useLetterFileDocumentApi();
const folderApi = useLetterFileFolderApi();

const letterFile = computed(() => letterFileStore.getCurrent());

const link = ref<string>("");
watch(
  () => props.modelValue,
  async () => {
    if (props.modelValue) {
      let linkPromise: Promise<string>;
      const purpose = props.mailto ? LinkPurpose.MAIL : LinkPurpose.DOWNLOAD;
      switch (props.type) {
        case "document":
          linkPromise = documentApi.getLink(props.id, letterFile.value!.id, purpose);
          break;
        case "folder":
          linkPromise = folderApi.generateArchive(props.id!, letterFile.value!.id, purpose);
          break;
        case "letterFile":
          linkPromise = letterFileStore.generateArchive(props.id!, purpose);
          break;
      }
      const theLink = await linkPromise;
      if (simpleFeatureStore.tmpDisableLinksRefacto || simpleFeatureStore.linkShortenerEnabled) {
        link.value = theLink;
      } else {
        link.value = `${theLink}&asAttachment=true`;
      }
      if (props.mailto) {
        const parsedUrl = new URL(link.value);
        const expiration = dayjs
          .tz(parsedUrl.searchParams.get("X-Amz-Date")?.slice(0, -1), "YYYYMMDDTHHmmss", "UTC")
          .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
          .add(simpleFeatureStore.linksDuration, "seconds")
          .format("DD/MM/YYYY à HH:mm");
        window.location = `mailto:?body=${encodeURIComponent(
          `Lien de téléchargement valable jusqu'au ${expiration} : ${link.value}`
        )}` as any;
        emit("update:modelValue", false);
      }
    } else {
      link.value = "";
    }
  }
);
</script>
